<script setup lang="ts">
import {ContestObjectiveProgress, ContestPerformerResult, ContestWithProgress} from "@/contest/interfaces";
import {ref, onMounted, computed} from "vue";
import {Trophy} from "@lucide/vue";

const props = defineProps<{ contest: ContestWithProgress }>();

const progress = ref<ContestObjectiveProgress | null>(null);
const topPerformer = ref<ContestPerformerResult | null>(null);
const loading = ref(false);

/**
 * On mount, load the performers data of a contest.
 */
onMounted(async () => {
    progress.value = props.contest.objective_progress;
    topPerformer.value = props.contest.top_performer;
});

const clampedPercentage = computed(() => {
    return Math.min(100, Math.max(0, progress.value?.percentage ?? 0));
});

const isComplete = computed(() => (progress.value?.percentage ?? 0) >= 100);
</script>

<template>
  <div v-if="loading" class="h-4 w-32 bg-gray-100 animate-pulse rounded-full" />

  <div v-else-if="progress" class="w-full min-w-[140px] md:max-w-[180px] lg:max-w-[280px]">
    <div class="flex items-center justify-between mb-1">
      <span class="text-xs text-gray-500 truncate">{{ contest.objective?.label ?? 'Objectif' }}</span>
      <span class="text-xs font-semibold ml-2 shrink-0" :class="isComplete ? 'text-green-600' : 'text-primary'">
        {{ progress.formatted }}
      </span>
    </div>
    <div class="relative h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div
        class="absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out"
        :class="isComplete ? 'bg-green-500' : 'bg-primary'"
        :style="{ width: `${clampedPercentage}%` }"
      />
    </div>
    <span v-if="isComplete" class="text-[10px] text-green-600 font-medium">🎯 Objectif atteint !</span>
  </div>

  <div
    v-else-if="topPerformer"
    class="flex flex-col gap-1.5 min-w-[140px] md:max-w-[180px] lg:max-w-[280px]"
  >
    <div class="flex items-center gap-1">
      <Trophy class="w-3.5 h-3.5 text-amber-500 shrink-0" />
      <span class="text-xs text-gray-700 truncate">{{ topPerformer.performer.name }}</span>
    </div>
    <span class="text-xs font-semibold text-primary shrink-0">{{ topPerformer.score.formatted }}</span>
  </div>

  <span v-else class="text-xs text-gray-400">—</span>
</template>

